import { useState, useCallback } from 'react';
import { API_CONFIG, buildApiUrl } from '@/lib/api-functions/api-config';

export interface ExportVersion {
  id: string;
  game_id: string;
  game_name: string;
  company_id: string;
  version_name: string;
  platform: string;
  template_name: string | null;
  storage_path: string;
  file_size: number;
  file_type: string;
  metadata: Record<string, any>;
  created_at: string;
  created_by: string;
  download_count: number;
  last_downloaded_at: string | null;
  is_public: boolean;
  download_url: string;
}

export interface SaveVersionParams {
  gameId: string;
  gameName: string;
  companyId: string;
  versionName: string;
  platform: string;
  templateName?: string;
  metadata?: Record<string, any>;
  isPublic?: boolean;
  file: Blob;
  filename: string;
}

export interface UseExportVersionsReturn {
  versions: ExportVersion[];
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
  fetchVersions: (gameId: string) => Promise<void>;
  saveVersion: (params: SaveVersionParams) => Promise<ExportVersion | null>;
  deleteVersion: (versionId: string) => Promise<boolean>;
  updateVersion: (versionId: string, updates: { version_name?: string; is_public?: boolean }) => Promise<ExportVersion | null>;
  downloadVersion: (version: ExportVersion) => void;
}

function getStoredToken(): string | null {
  return localStorage.getItem('auth_token');
}

export function useExportVersions(): UseExportVersionsReturn {
  const [versions, setVersions] = useState<ExportVersion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchVersions = useCallback(async (gameId: string) => {
    setIsLoading(true);
    setError(null);

    try {
      const token = getStoredToken();
      const url = buildApiUrl(`/exports/versions?game_id=${encodeURIComponent(gameId)}`);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to fetch versions');
      }

      setVersions(data.data || []);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch versions';
      setError(errorMessage);
      console.error('Fetch versions error:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const saveVersion = useCallback(async (params: SaveVersionParams): Promise<ExportVersion | null> => {
    setIsSaving(true);
    setError(null);

    try {
      const token = getStoredToken();
      const url = buildApiUrl('/exports/save-version');
      
      // Create FormData for multipart upload
      const formData = new FormData();
      formData.append('game_id', params.gameId);
      formData.append('game_name', params.gameName);
      formData.append('company_id', params.companyId);
      formData.append('version_name', params.versionName);
      formData.append('platform', params.platform);
      if (params.templateName) {
        formData.append('template_name', params.templateName);
      }
      if (params.metadata) {
        formData.append('metadata', JSON.stringify(params.metadata));
      }
      formData.append('is_public', String(params.isPublic || false));
      formData.append('file', params.file, params.filename);

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
          // Don't set Content-Type - let browser set it with boundary for multipart
        },
        body: formData,
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to save version');
      }

      const newVersion = data.data as ExportVersion;
      
      // Add to versions list
      setVersions(prev => [newVersion, ...prev]);
      
      return newVersion;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to save version';
      setError(errorMessage);
      console.error('Save version error:', err);
      return null;
    } finally {
      setIsSaving(false);
    }
  }, []);

  const deleteVersion = useCallback(async (versionId: string): Promise<boolean> => {
    setError(null);

    try {
      const token = getStoredToken();
      const url = buildApiUrl(`/exports/version?id=${encodeURIComponent(versionId)}`);
      
      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to delete version');
      }

      // Remove from versions list
      setVersions(prev => prev.filter(v => v.id !== versionId));
      
      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete version';
      setError(errorMessage);
      console.error('Delete version error:', err);
      return false;
    }
  }, []);

  const updateVersion = useCallback(async (
    versionId: string, 
    updates: { version_name?: string; is_public?: boolean }
  ): Promise<ExportVersion | null> => {
    setError(null);

    try {
      const token = getStoredToken();
      const url = buildApiUrl(`/exports/version?id=${encodeURIComponent(versionId)}`);
      
      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(updates),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to update version');
      }

      const updatedVersion = data.data as ExportVersion;
      
      // Update in versions list
      setVersions(prev => prev.map(v => v.id === versionId ? { ...v, ...updatedVersion } : v));
      
      return updatedVersion;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update version';
      setError(errorMessage);
      console.error('Update version error:', err);
      return null;
    }
  }, []);

  const downloadVersion = useCallback((version: ExportVersion) => {
    if (version.download_url) {
      // Create a temporary link and trigger download
      const link = document.createElement('a');
      link.href = version.download_url;
      link.download = `${version.version_name}.${version.file_type}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }, []);

  return {
    versions,
    isLoading,
    isSaving,
    error,
    fetchVersions,
    saveVersion,
    deleteVersion,
    updateVersion,
    downloadVersion,
  };
}
